import { useEffect, useRef, useState } from "react";

import { exchangeWorkspaceGoogle } from "./api";
import { publishResultAndClose } from "./runtime";

export function useWorkspaceOAuthCallback(): string {
  const [status, setStatus] = useState("Completing Magnis Cloud sign-in...");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const completeLogin = async (): Promise<void> => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");
      const providerError = params.get("error");
      const expectedState = localStorage.getItem("workspace_google_state");
      const baseUrl = localStorage.getItem("workspace_google_base_url");
      const workspaceId = localStorage.getItem(
        "workspace_google_workspace_id",
      );

      if (workspaceId == null) {
        setStatus("Magnis Cloud sign-in failed: missing workspace.");
        return;
      }

      if (providerError != null) {
        // @tested-by: tst_fe_workspace_oauth_002
        await publishResultAndClose({
          type: "workspace_auth_result",
          workspaceId,
          success: false,
          error: providerError,
        });
        setStatus(`Authorization failed: ${providerError}`);
        return;
      }

      // @tested-by: tst_fe_workspace_oauth_001
      // @invariant: only the state issued for this popup may be exchanged.
      if (
        code == null ||
        state == null ||
        expectedState == null ||
        state !== expectedState ||
        baseUrl == null
      ) {
        // @tested-by: tst_fe_workspace_oauth_003
        await publishResultAndClose({
          type: "workspace_auth_result",
          workspaceId,
          success: false,
          error: "invalid_workspace_oauth_callback",
        });
        setStatus("Magnis Cloud sign-in failed: invalid callback.");
        return;
      }

      try {
        const data = await exchangeWorkspaceGoogle(baseUrl, code, state);
        if (data.error || !data.token || !data.user) {
          // @tested-by: tst_fe_workspace_oauth_004
          const error = data.error ?? "google_workspace_exchange_failed";
          await publishResultAndClose({
            type: "workspace_auth_result",
            workspaceId,
            success: false,
            error,
          });
          setStatus(`Magnis Cloud sign-in failed: ${error}`);
          return;
        }

        localStorage.removeItem("workspace_google_state");
        localStorage.removeItem("workspace_google_base_url");
        localStorage.removeItem("workspace_google_workspace_id");
        await publishResultAndClose({
          type: "workspace_auth_result",
          workspaceId,
          success: true,
          token: data.token,
          user: data.user,
        });
        setStatus("Connected. Closing this window...");
      } catch (error: unknown) {
        // @tested-by: tst_fe_workspace_oauth_005
        const message =
          error instanceof Error
            ? error.message
            : "google_workspace_exchange_failed";
        await publishResultAndClose({
          type: "workspace_auth_result",
          workspaceId,
          success: false,
          error: message,
        });
        setStatus(`Magnis Cloud sign-in failed: ${message}`);
      }
    };

    void completeLogin();
  }, []);

  return status;
}
